import React, { useState } from "react";
import logo from "../assets/scenathon_logo_short.png";
import logoFable from "../assets/logo_fable.webp";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import iconScenathon from "../assets/icons/scenathon.svg";
import iconDashboard from "../assets/icons/dashboard.svg";
import iconInitialSubmission from "../assets/icons/initial_submission.svg";
import iconLogout from "../assets/icons/logout.svg";
import iconAdmin from "../assets/icons/admin.svg";
import iconTrade from "../assets/icons/trade_adjusment.svg";
import iconTradeData from "../assets/icons/trade_adjusment_data.svg";
import { useAuth } from "../auth/AuthContext";
import { useRole } from "../auth/useRole";
import { ShowIfHasSection } from "../auth/RoleComponents";

const API = process.env.REACT_APP_API_URL;
const SIDEBAR_W = "w-[302px]";

export default function Sidebar() {
  const [open, setOpen] = useState(false);

  const location = useLocation();
  const { SECTIONS, permissionsLoaded } = useRole();
  const navigate = useNavigate();
  const { logout, accessToken, csrfToken, selectedScenathon, authFetch } = useAuth();

  const isActive = (path) => location.pathname.startsWith(path);
  const close = () => setOpen(false);

  const handleLogout = async () => {
    try {
      await logout?.();
    } catch (e) {
      console.warn("AuthContext logout failed:", e);
      try {
        await fetch(`${API}/api/auth/logout`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(csrfToken ? { "y-csrf-token": csrfToken } : {}),
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({}),
        });
      } catch (err) {
        console.warn("Logout endpoint failed:", err);
      }
    } finally {
      close();
      navigate("/", { replace: true });
    }
  };

  const linkCls = (path) =>
    `flex items-center gap-3 text-left py-3 px-4 rounded-md font-medium transition duration-150 ${isActive(path) ? "bg-[#03203d] text-white" : "hover:bg-gray-100"
    }`;

  const iconCls = (path) =>
    `w-5 h-5 ${isActive(path) ? "invert brightness-0" : ""}`;

  return (
    <>
      {/* ✅ Hamburger SOLO mobile y se oculta al abrir */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`lg:hidden fixed top-4 left-4 z-[60] h-10 w-10 rounded-lg bg-white shadow ring-1 ring-gray-200 items-center justify-center ${open ? "hidden" : "flex"
          }`}
        aria-label="Open menu"
      >
        <span className="text-xl leading-none">☰</span>
      </button>

      {/* ✅ Overlay SOLO mobile */}
      <div
        onClick={close}
        className={`lg:hidden fixed inset-0 z-50 bg-black/40 transition-opacity ${open ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
      />

      {/* ✅ Sidebar: desktop fixed; mobile drawer */}
      <aside
        className={`
          ${SIDEBAR_W}
          bg-white rounded-md p-6 flex flex-col overflow-y-auto
          h-screen fixed top-0 left-0 z-[55]

          transform transition-transform duration-200 ease-out
          ${open ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0
        `}
      >
        <div className="mb-8">
          <div className="flex items-center justify-between gap-4">
            <div className="flex gap-5">
            <img src={logo} alt="logo" className="w-[100px] object-contain" />
            <img src={logoFable} alt="logo" className="w-[100px] object-contain" />
            </div>
            <button
              type="button"
              onClick={close}
              className="lg:hidden h-9 w-9 rounded-lg hover:bg-gray-100 flex items-center justify-center"
              aria-label="Close menu"
            >
              ✕
            </button>
          </div>
        </div>

        <nav className="flex flex-col gap-3 flex-1">
          {permissionsLoaded && (
            <>

              <ShowIfHasSection section={SECTIONS.INITIAL_SUBMISSION}>
                <Link
                  to="/initial-submission"
                  onClick={close}
                  className={linkCls("/initial-submission")}
                >
                  <img
                    src={iconInitialSubmission}
                    alt="icon"
                    className={iconCls("/initial-submission")}
                  />
                  Initial Submission
                </Link>
              </ShowIfHasSection>

              <ShowIfHasSection section={SECTIONS.SUBMISSION}>
                <Link
                  to="/submission"
                  onClick={close}
                  className={linkCls("/submission")}
                >
                  <img
                    src={iconScenathon}
                    alt="icon"
                    className={iconCls("/submission")}
                  />
                  Live Submission
                </Link>
              </ShowIfHasSection>

              <ShowIfHasSection section={SECTIONS.DASHBOARD}>
                <Link
                  to="/dashboard"
                  onClick={close}
                  className={linkCls("/dashboard")}
                >
                  <img
                    src={iconDashboard}
                    alt="icon"
                    className={iconCls("/dashboard")}
                  />
                  Dashboard
                </Link>
              </ShowIfHasSection>

              <ShowIfHasSection section={SECTIONS.TRADE_ADJUSTMENT}>
                <Link
                  to="/trade-adjustment"
                  onClick={close}
                  className={linkCls("/trade-adjustment")}
                >
                  <img
                    src={iconTrade}
                    alt="icon"
                    className={iconCls("/trade-adjustment")}
                  />
                  Trade Adjustment
                </Link>
              </ShowIfHasSection>

              <ShowIfHasSection section={SECTIONS.DATA_TRADE_ADJUSTMENT}>
                <Link
                  to="/data-trade-adjustment"
                  onClick={close}
                  className={linkCls("/data-trade-adjustment")}
                >
                  <img
                    src={iconTradeData}
                    alt="icon"
                    className={iconCls("/data-trade-adjustment")}
                  />
                  Data Trade Adjustment
                </Link>
              </ShowIfHasSection>
            </>
          )}
        </nav>

        {selectedScenathon?.id && (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Scenathon active
            </p>
            <p className="mt-1 truncate font-semibold text-slate-900">
              {selectedScenathon.name || `ID ${selectedScenathon.id}`}
            </p>
          </div>
        )}

        <ShowIfHasSection section={SECTIONS.ADMIN_SCENATHON}>
          {permissionsLoaded ? (
            <Link to="/admin/scenathon"
              onClick={close}
              className={linkCls("/admin/scenathon")}>
              <img src={iconAdmin} alt="scenathon" className={iconCls("/admin/scenathon")} />
              Admin</Link>
          ) : null}
        </ShowIfHasSection>

        <button
          type="button"
          onClick={handleLogout}
          className="flex items-center gap-3 text-left py-3 px-4 rounded-md font-medium hover:bg-gray-100 transition duration-150"
        >
          <img src={iconLogout} alt="logout" className="w-5 h-5" />
          Logout
        </button>

      </aside>
    </>
  );
}
